import { ITableRowProps } from '@components/Table/Table';
import { Wrapper, Cell, NavItem, ActionButton } from './RowTable.style';
import { FileItem, ISelectionItem, NameFileType, selectionsApiSlice } from '@src/api';
import { FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Popover,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { ReactComponent as ArrowRight } from '@icons/Arrows/right.svg';
import { ReactComponent as ArrowDown } from '@icons/Arrows/down.svg';
import { ReactComponent as DeleteIcon } from '@icons/TableRow/delete.svg';
import { ReactComponent as CopyIcon } from '@icons/TableRow/copy.svg';
import { ReactComponent as NotificationIcon } from '@icons/TableRow/notification.svg';
import { ReactComponent as CreateOrdersIcon } from '@icons/TableRow/create_orders.svg';
import { ReactComponent as ArowDownIcon } from '@icons/Arrows/down-sm.svg';
import {
  border,
  buildDataTestIdAttribute,
  buildFullDataTestId,
  compareFunctionForSort,
  getDisplayDate,
  hexOpacity,
  log,
  replaceCebabCase,
} from '@src/utils';
import { theme } from '@src/theme';
import { Modal } from '@components/Modal';
import { ButtonGroup } from '@components/ButtonGroup';
import { useNavigate } from 'react-router-dom';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { Axios } from '@src/api/common';
import { PortalContext } from '@src/context/portal';
import { RoleKeeper } from '@components/RoleKeeper';
import { Tooltip } from '@components/Tooltip';

export type B2CNotificationData = {
  description: string;
  generationTaskId?: number;
  patientsCount?: number;
};

export type B2BNotificationData = {
  description: string;
  selectionId: number;
};

const CONTENT_DISPOSITION_HEADER = 'content-disposition';

const GeneratedFiles = ({
  files,
  patientsCount,
  selectionId,
  onClose,
  dataTestId,
}: {
  files: FileItem[];
  patientsCount?: number;
  selectionId?: number;
  onClose: () => void;
  dataTestId?: string;
}) => {
  const [sortedFiles, setSortedFiles] = useState<FileItem[]>([]);

  useEffect(() => {
    if (files) {
      const copyFilesForSort = [...files];
      copyFilesForSort?.sort((currentFile, nextFile) =>
        compareFunctionForSort(NameFileType[currentFile?.fileType], NameFileType[nextFile?.fileType]),
      );
      setSortedFiles(copyFilesForSort);
    }
  }, [files]);

  const downloadFile = async (fileId, name: string) => {
    onClose();
    if (selectionId && fileId) {
      const result = await Axios<Blob>({
        url: `${
          window.env?.REACT_APP_SELECTION_BASE_URL ?? process.env.REACT_APP_SELECTION_BASE_URL
        }/api/v1/selections/${selectionId}/result-files/${fileId}`,
        method: 'GET',
        responseType: 'blob',
      });
      const fileName = result.headers[CONTENT_DISPOSITION_HEADER]
        ? decodeURIComponent((result.headers[CONTENT_DISPOSITION_HEADER] as string).split(';')?.[1]?.split('=')?.[1])
        : name;

      const aElement = document.createElement('a');
      aElement.setAttribute('download', fileName);
      const href = URL.createObjectURL(result.data);
      aElement.href = href;
      aElement.setAttribute('target', '_blank');
      aElement.click();
    }
  };

  return (
    <List sx={{ padding: 0, minWidth: '180px' }}>
      {sortedFiles.map(({ fileId, fileType }) => {
        const name = `${NameFileType[fileType]}, ${patientsCount} patients`;
        return (
          <ListItem
            disablePadding
            key={fileId}
            {...buildDataTestIdAttribute(dataTestId, replaceCebabCase(fileType as string))}
          >
            <ListItemButton sx={{ padding: '12px' }} onClick={() => downloadFile(fileId, name)}>
              <Typography variant="label14Medium" lineHeight="18px">
                {NameFileType[fileType]}
              </Typography>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

const autoHeight = 'auto!important';

type CustomTooltipProps = {
  title: string;
  disabled: boolean;
  children: ReactNode;
  width?: string;
  padding?: string;
};

export const CustomTooltip: FC<CustomTooltipProps> = ({
  title,
  disabled,
  children,
  width = '150px',
  padding = '6px 9px',
}) => (
  <Tooltip
    title={disabled ? title : ''}
    arrow
    placement="top"
    backgroundColor={theme.palette.grey['700']}
    componentsProps={{
      tooltip: {
        sx: {
          padding,
        },
      },
    }}
    PopperProps={{
      disablePortal: true,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -16],
          },
        },
        {
          name: 'flip',
          enabled: true,
        },
      ],
      sx: {
        lineHeight: '18px',
        width,
        textAlign: 'left',
      },
    }}
    sx={{
      fontSize: '12px',
      zIndex: 10,
    }}
  >
    <Box sx={{ backgroundColor: 'transparent' }}>{children}</Box>
  </Tooltip>
);

type RowTableProps = ITableRowProps<ISelectionItem> & {
  setOpenGenerateModal?: ({ id, name }: { id: number; name: string }) => void;
  setB2cNotificationData?: (val: B2CNotificationData) => void;
  setB2bNotificationData?: (val: B2BNotificationData) => void;
  activeNotificationsIds?: number[];
  showSharedActionIconsSx?: SxProps;
  showDeleteIconSx?: SxProps;
  canCreateOrders?: boolean;
  setOpenCreateOrder?: (val: number) => void;
};

const DATA_TEST_ID_MODAL_DELETE = 'modal-delete-selection';
export const RowSelection: FC<RowTableProps> = ({
  data,
  gridTemplateColumns,
  setB2cNotificationData,
  setB2bNotificationData,
  setOpenGenerateModal,
  activeNotificationsIds,
  index,
  dataTestId,
  showSharedActionIconsSx,
  showDeleteIconSx,
  canCreateOrders,
  setOpenCreateOrder,
}) => {
  const navigate = useNavigate();
  const { module } = useContext(PortalContext);
  const ref = useRef(null);
  const [deleteSelection, { isLoading: isLoadingDelete }] = selectionsApiSlice.useDeleteSelectionMutation();
  const [open, setOpen] = useState(false);
  const [opendeleteModal, setOpenDeleteModal] = useState(false);
  const [openGeneratedFiles, setOpenGeneratedFiles] = useState(false);

  const onClickArrow = () => {
    setOpen(prev => !prev);
  };

  const onDelete = () => {
    deleteSelection(data.id)
      .then((res: any) => res.data.status === 200 && setOpenDeleteModal(false))
      .catch(log);
  };

  const changeOpenDeleteModalOpen = () => {
    setOpenDeleteModal(prev => !prev);
  };

  const goCopy = () => {
    navigate(`/copy/${data?.id}`);
  };

  const openNotificationModal = () => {
    setB2cNotificationData &&
      setB2cNotificationData({
        description: data?.patientDescription || '',
        generationTaskId: data.generationTaskDetails?.generationTaskId,
        patientsCount: data.generationTaskDetails?.patientsCount,
      });
    setB2bNotificationData &&
      setB2bNotificationData({
        description: data?.internalDescription,
        selectionId: data.id,
      });
  };

  const disabledNotificationB2C =
    !data?.generationTaskDetails?.generationTaskId ||
    (activeNotificationsIds || [])?.includes(data?.id) ||
    data?.generationTaskDetails?.patientsCount === 0;

  const disabledNotificationB2B = !data.id || ['ForMe', 'ForOneSalesRepresentative'].includes(data.visibilityType);

  const disabledNotification = module === 'LIS' ? disabledNotificationB2B : disabledNotificationB2C;
  const disabledDeleteButton = !data?.isEditable;

  const openWarningModal = () => {
    data?.id && setOpenGenerateModal && setOpenGenerateModal({ id: data.id, name: data.name || '-' });
  };

  const handleOpenGeneratedFiles = () => {
    setOpenGeneratedFiles(true);
  };

  const handleCloseGeneratedFiles = () => {
    setOpenGeneratedFiles(false);
  };

  const fileItemsIsNotEmpty =
    data?.generationTaskDetails?.selectionResultFileItems &&
    data.generationTaskDetails.selectionResultFileItems.length > 0;

  const patientCount = data?.generationTaskDetails?.patientsCount;

  const handleOpenCreateOrderModal = () => {
    setOpenCreateOrder && setOpenCreateOrder(data.id);
  };

  return (
    <Stack
      border={border}
      sx={{
        backgroundColor: '#fff',
        '&:hover': {
          backgroundColor: '#f7fcfd',
        },
      }}
      {...buildDataTestIdAttribute(dataTestId)}
    >
      <Wrapper sx={{ gridTemplateColumns: `8px ${gridTemplateColumns}` }}>
        <div />
        <Cell sx={{ gap: '12px', minWidth: '130px' }}>
          <IconButton
            sx={showSharedActionIconsSx ? { padding: '3px', ...showSharedActionIconsSx } : { padding: '3px' }}
            onClick={onClickArrow}
            disabled={!data.internalDescription}
            {...buildDataTestIdAttribute(dataTestId, 'button-open-description')}
          >
            {open ? <ArrowDown /> : <ArrowRight />}
          </IconButton>
          {getDisplayDate(data?.lastEdit) || '–'}
        </Cell>
        <NavItem to={`/${data?.id}`} {...buildDataTestIdAttribute(dataTestId, 'link-open-selection')}>
          {data?.name}
        </NavItem>
        <Cell sx={{ gap: '12px', flexShrink: 0, textWrap: 'nowrap' }}>
          {data?.generationTaskDetails && !data?.hasActiveGenerationTask && fileItemsIsNotEmpty ? (
            <Stack direction="row" gap="24px" alignItems="center" width="80%" justifyContent="end">
              <Typography variant="label14Medium" lineHeight="18px" /* flexGrow={1} */ textAlign="end">{`${
                patientCount || 0
              } patients`}</Typography>
              {patientCount && fileItemsIsNotEmpty ? (
                <Box sx={{ padding: '2px 0' }} ref={ref}>
                  <ActionButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenGeneratedFiles}
                    endIcon={<ArowDownIcon />}
                    {...buildDataTestIdAttribute(dataTestId, 'button-download')}
                  >
                    Download
                  </ActionButton>
                  <Popover
                    open={openGeneratedFiles}
                    anchorEl={ref.current}
                    onClose={handleCloseGeneratedFiles}
                    disablePortal
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <GeneratedFiles
                      files={data?.generationTaskDetails?.selectionResultFileItems || []}
                      selectionId={data?.id}
                      patientsCount={data?.generationTaskDetails?.patientsCount}
                      onClose={handleCloseGeneratedFiles}
                      dataTestId={dataTestId}
                    />
                  </Popover>
                </Box>
              ) : (
                <Box sx={{ maxWidth: '102px', width: '100%', height: '1px' }}> </Box>
              )}
            </Stack>
          ) : (
            '–'
          )}
        </Cell>
        <Cell sx={{ height: autoHeight, textWrap: 'nowrap' }}>
          {data?.hasActiveGenerationTask
            ? '–'
            : getDisplayDate(data?.expirationDate) ||
              getDisplayDate(data?.generationTaskDetails?.selectionResultFileItems[0]?.expirationDate || '') ||
              '–'}
        </Cell>
        <Cell sx={{ minWidth: '102px', justifyContent: 'center' }}>
          {data?.hasActiveGenerationTask ? (
            <PreloaderBubbles size={7} sx={{ backgroundColor: 'transparent' }} />
          ) : (
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={openWarningModal}
              {...buildDataTestIdAttribute(dataTestId, 'button-generate')}
            >
              Generate file
            </ActionButton>
          )}
        </Cell>
        <Cell
          sx={{
            justifyContent: 'end',
            height: autoHeight,
            position:
              index === 0 && ((disabledNotificationB2B && module === 'LIS') || disabledDeleteButton)
                ? 'static !important'
                : '',
          }}
        >
          {patientCount && fileItemsIsNotEmpty && module !== 'LIS' ? (
            <CustomTooltip
              title={canCreateOrders ? 'Create orders' : 'No connected labs'}
              disabled
              width={canCreateOrders ? '95px' : '125px'}
            >
              <IconButton
                onClick={handleOpenCreateOrderModal}
                disabled={disabledNotification}
                sx={showSharedActionIconsSx ?? null}
                {...buildDataTestIdAttribute(dataTestId, 'button-create-order')}
              >
                <CreateOrdersIcon />
              </IconButton>
            </CustomTooltip>
          ) : (
            <Box width="42px" />
          )}
          <RoleKeeper permissions={['notification.orderingprovider.create', 'notification.patient.create']}>
            <CustomTooltip
              title="There are no ordering providers in this selection"
              disabled={disabledNotificationB2B && module === 'LIS'}
              width="180px"
            >
              <IconButton
                onClick={openNotificationModal}
                disabled={disabledNotification}
                sx={showSharedActionIconsSx ?? null}
                {...buildDataTestIdAttribute(dataTestId, 'button-notification')}
              >
                <NotificationIcon />
              </IconButton>
            </CustomTooltip>
          </RoleKeeper>

          <RoleKeeper permissions="selections.copy">
            <IconButton
              onClick={goCopy}
              sx={showSharedActionIconsSx ?? null}
              {...buildDataTestIdAttribute(dataTestId, 'button-copy')}
            >
              <CopyIcon />
            </IconButton>
          </RoleKeeper>
          <RoleKeeper permissions="selections.delete">
            <CustomTooltip
              title="You do not have permission to delete this selection."
              disabled={disabledDeleteButton}
              padding="6px 18px 6px 9px"
            >
              <IconButton
                onClick={changeOpenDeleteModalOpen}
                disabled={disabledDeleteButton}
                sx={showDeleteIconSx ?? null}
                {...buildDataTestIdAttribute(dataTestId, 'button-delete')}
              >
                <DeleteIcon />
              </IconButton>
            </CustomTooltip>
          </RoleKeeper>
        </Cell>
      </Wrapper>
      <Collapse in={open}>
        <Stack
          direction="row"
          columnGap="12px"
          rowGap="4px"
          ml="24px"
          borderTop={`1px solid ${hexOpacity(theme.palette.secondary.main, '14%')}`}
          padding="12px 24px 12px 36px"
          flexWrap="wrap"
          sx={{
            backgroundColor: '#FFFFFF',
          }}
        >
          <Typography color="grey.700" variant="label12Bold" lineHeight="16px">
            Description
          </Typography>
          <Typography color="grey.700" variant="label12Medium" lineHeight="16px">
            {data?.internalDescription}
          </Typography>
        </Stack>
      </Collapse>
      <Modal
        open={opendeleteModal}
        title="Delete selection"
        onCancel={changeOpenDeleteModalOpen}
        wrapperProps={{
          PaperProps: {
            sx: {
              maxWidth: '400px',
              width: '100%',
            },
            ...buildDataTestIdAttribute(buildFullDataTestId(DATA_TEST_ID_MODAL_DELETE)),
          },
        }}
      >
        <Typography
          variant="body16Medium"
          color="grey.700"
          lineHeight="24px"
          mb="36px"
          component="p"
        >{`Would you like to delete “${data.name}” selection?`}</Typography>

        <ButtonGroup
          onApply={onDelete}
          onCancel={changeOpenDeleteModalOpen}
          applyName="Yes"
          cancelName="No"
          disabled={isLoadingDelete}
          dataTestId={buildFullDataTestId(DATA_TEST_ID_MODAL_DELETE)}
        />
      </Modal>
    </Stack>
  );
};
